import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

// Layout Components
import TopBar from "@/components/layout/TopBar";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import SEOHead from "@/components/SEOHead";

// Pages
import Home from "@/pages/Home";
import OurCompany from "@/pages/OurCompany";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import ProjectGallery from "@/pages/ProjectGallery";
import Careers from "@/pages/Careers";
import WebsiteAccessibility from "@/pages/WebsiteAccessibility";
import Reviews from "@/pages/Reviews";
import TermsConditions from "@/pages/TermsConditions";
import PrivacyPolicy from "@/pages/PrivacyPolicy";

// Service Sub-pages
import ResidentialService from "@/pages/services/ResidentialService";
import CommercialService from "@/pages/services/CommercialService";
import BoilersService from "@/pages/services/BoilersService";
import WaterHeatersService from "@/pages/services/WaterHeatersService";
import AirConditioningService from "@/pages/services/AirConditioningService";
import RemodelingService from "@/pages/services/RemodelingService";

// City SEO Pages
import SaintPaulPage from "@/pages/cities/SaintPaulPage";
import MinneapolisPage from "@/pages/cities/MinneapolisPage";
import OakdalePage from "@/pages/cities/OakdalePage";
import WoodburyPage from "@/pages/cities/WoodburyPage";
import EaganPage from "@/pages/cities/EaganPage";
import MaplewoodPage from "@/pages/cities/MaplewoodPage";
import CottageGrovePage from "@/pages/cities/CottageGrovePage";
import LakeElmoPage from "@/pages/cities/LakeElmoPage";
import NewportPage from "@/pages/cities/NewportPage";
import RosemountPage from "@/pages/cities/RosemountPage";

// Blog Pages
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";

// Accessibility Widget
import AccessibilityWidget from "@/components/AccessibilityWidget";

// Geo Blocker
import GeoBlocker from "@/components/GeoBlocker";
import { SiteTracking } from "@/components/SiteTracking";

// Admin
import { AdminAuthProvider, RequireAdmin } from "@/context/AdminAuth";
import { AdminLayout } from "@/components/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminCalls from "@/pages/admin/AdminCalls";

const AdminRoot = () => (
  <AdminAuthProvider>
    <Outlet />
  </AdminAuthProvider>
);

// Layout wrapper component
const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <TopBar />
      <Header />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <GeoBlocker>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/dashboard" element={<AdminRoot />}>
                <Route path="login" element={<AdminLogin />} />
                <Route element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                  <Route index element={<Navigate to="leads" replace />} />
                  <Route path="leads" element={<AdminLeads />} />
                  <Route path="calls" element={<AdminCalls />} />
                </Route>
              </Route>
              <Route path="/admin/*" element={<Navigate to="/dashboard/leads" replace />} />
              <Route path="*" element={<PublicSite />} />
            </Routes>
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </GeoBlocker>
      </div>
    </HelmetProvider>
  );
}

const PublicSite = () => (
  <>
    <SiteTracking />
    <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/our-company" element={<OurCompany />} />
                <Route path="/services" element={<Services />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/project-gallery" element={<ProjectGallery />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/website-accessibility" element={<WebsiteAccessibility />} />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/terms-conditions" element={<TermsConditions />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                {/* Service sub-routes */}
                <Route path="/services/residential" element={<ResidentialService />} />
                <Route path="/services/commercial" element={<CommercialService />} />
                <Route path="/services/boilers" element={<BoilersService />} />
                <Route path="/services/water-heaters" element={<WaterHeatersService />} />
                <Route path="/services/air-conditioning" element={<AirConditioningService />} />
                <Route path="/services/remodeling" element={<RemodelingService />} />
                {/* City SEO Pages */}
                <Route path="/plumber-saint-paul" element={<SaintPaulPage />} />
                <Route path="/plumber-minneapolis" element={<MinneapolisPage />} />
                <Route path="/plumber-oakdale" element={<OakdalePage />} />
                <Route path="/plumber-woodbury" element={<WoodburyPage />} />
                <Route path="/plumber-eagan" element={<EaganPage />} />
                <Route path="/plumber-maplewood" element={<MaplewoodPage />} />
                <Route path="/plumber-cottage-grove" element={<CottageGrovePage />} />
                <Route path="/plumber-lake-elmo" element={<LakeElmoPage />} />
                <Route path="/plumber-newport" element={<NewportPage />} />
                <Route path="/plumber-rosemount" element={<RosemountPage />} />
                {/* Blog Pages */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
              </Routes>
    </Layout>
    <AccessibilityWidget />
  </>
);

export default App;
